import React, { useState, useMemo, useEffect } from 'react'
import { isMobile } from 'react-device-detect'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faPlus, faRefresh } from '@fortawesome/free-solid-svg-icons'
import mobileSubheader from '../_layout/elements/mobile-subheader'
import affiliateService from '../../../services/affilate.serrvice'

interface SettlementItem {
  _id: string
  affiliateId?: string
  referralCode?: string
  createdAt: string
  date?: string
  narration: string
  type: 'deposit_commission' | 'profit_commission' | 'payout' | 'adjustment'
  credit: number
  debit: number
  balanceAfter: number
  reference?: string
  addedBy?: string
}

interface Affiliate {
  _id: string
  fullName: string
  referralCode: string
}

const AffilateTransection: React.FC = () => {
  const [affiliate, setAffiliate] = useState<Affiliate | null>(null)
  const [items, setItems] = useState<SettlementItem[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [amount, setAmount] = useState('')
  const [settleType, setSettleType] = useState<'payout' | 'adjustment'>('payout')
  const [remarks, setRemarks] = useState('')
  const [loading, setLoading] = useState(false)

  // 🔹 Fetch account statement from backend
  useEffect(() => {
    const fetchAccountStatement = async () => {
      setLoading(true)
      try {
        const res = await affiliateService.getAccStPanel()
        console.log('API Response:', res)
        if (res.data && !res.data.error) {
          // API returns array of settlements
          const settlements: SettlementItem[] = res.data.data || []

          if (settlements.length > 0) {
            setAffiliate({
              _id: settlements[0].affiliateId || '',
              fullName: 'Affiliate', // Replace with actual name if API provides
              referralCode: settlements[0].referralCode || ''
            })
          }

          setItems(settlements)
        } else {
          toast.error(res.data?.message || 'Failed to load account statement')
        }
      } catch (err) {
        console.error(err)
        toast.error('Failed to fetch account statement')
      } finally {
        setLoading(false)
      }
    }

    fetchAccountStatement()
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
  }

  const handleAddSettlement = () => {
    const amt = parseFloat(amount)
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount')
      return
    }

    const newBalance = items.length > 0 ? items[items.length - 1].balanceAfter : 0
    const newSettlement: SettlementItem = {
      _id: Date.now().toString(),
      affiliateId: affiliate?._id,
      date: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      referralCode: affiliate?.referralCode,
      narration: `${settleType === 'payout' ? 'Payout' : 'Adjustment'}: ${remarks}`,
      type: settleType,
      credit: settleType === 'adjustment' ? amt : 0,
      debit: settleType === 'payout' ? amt : 0,
      balanceAfter: newBalance + (settleType === 'adjustment' ? amt : -amt),
      reference: `${settleType.toUpperCase()}${Date.now()}`,
      addedBy: 'Admin'
    }
    setItems([...items, newSettlement])
    setShowAdd(false)
    setAmount('')
    setRemarks('')
    toast.success('Settlement added successfully')
  }

  // 🔹 Filtered items
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch = searchTerm
        ? item.narration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.reference?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
          (item.addedBy?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
        : true

      const itemDate = (item.date || item.createdAt || '').split('T')[0]
      const matchesStartDate = startDate ? itemDate >= startDate : true
      const matchesEndDate = endDate ? itemDate <= endDate : true

      return matchesSearch && matchesStartDate && matchesEndDate
    })
  }, [items, searchTerm, startDate, endDate])

  const totalPaid = useMemo(
    () => filteredItems.reduce((sum, it) => sum + (it.debit || 0), 0),
    [filteredItems]
  )

  const totalCredited = useMemo(
    () => filteredItems.reduce((sum, it) => sum + (it.credit || 0), 0),
    [filteredItems]
  )

  const runningBalance = useMemo(
    () => (filteredItems.length > 0 ? filteredItems[filteredItems.length - 1].balanceAfter : 0),
    [filteredItems]
  )

  return (
    <>
      {mobileSubheader.subheaderdesktopadmin('Affiliate Settlement')}
      <div className='container-fluid'>
        <div className='row'>
          <div className={!isMobile ? 'col-md-12 mt-1' : 'col-md-12 padding-custom'}>
            <div className='card' style={{ marginTop: '10px' }}>
              <div className='card-header bg-primary text-white d-flex justify-content-between align-items-center'>
                <div>{affiliate?.fullName} — Settlement Statement</div>
                <div className='d-flex gap-2'>
                  <button className='btn btn-light btn-sm' onClick={() => window.location.reload()}>
                    <FontAwesomeIcon icon={faRefresh} />
                  </button>
                 
                </div>
              </div>

              <div className='card-body'>
                {/* Filters */}
                <form className='row g-3 mb-3' onSubmit={handleSearch}>
                  <div className='col-lg-3'>
                    <label className='form-label'>Start Date</label>
                    <input
                      type='date'
                      className='form-control'
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className='col-lg-3'>
                    <label className='form-label'>End Date</label>
                    <input
                      type='date'
                      className='form-control'
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className='col-lg-4'>
                    <label className='form-label'>Search</label>
                    <div className='input-group'>
                      <span className='input-group-text'>
                        <FontAwesomeIcon icon={faSearch} />
                      </span>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Search transactions...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className='col-lg-2 d-flex align-items-end'>
                    <button type='submit' className='btn btn-primary w-100'>
                      <FontAwesomeIcon icon={faSearch} />
                    </button>
                  </div>
                </form>

                {/* Summary */}
                <div className='row g-3 mb-3'>
                  <div className='col-md-3'>
                    <div className='card h-100'>
                      <div className='card-body'>
                        <div className='text-muted'>Total Commission</div>
                        <div className='fw-bold'>₹ {totalCredited.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='card h-100'>
                      <div className='card-body'>
                        <div className='text-muted'>Total Paid</div>
                        <div className='fw-bold'>₹ {totalPaid.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='card h-100'>
                      <div className='card-body'>
                        <div className='text-muted'>Current Balance</div>
                        <div className='fw-bold'>₹ {runningBalance.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transactions Table */}
                <div className='table-responsive'>
                  <table className='table table-hover'>
                    <thead className='table-dark'>
                      <tr>
                        <th>Date</th>
                        <th>Remark</th>
                        <th>Type</th>
                        <th className='text-end'>Credit</th>
                        <th className='text-end'>Debit</th>
                        <th className='text-end'>Balance</th>
                        <th>Reference</th>
                        <th>Added By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.length === 0 ? (
                        <tr>
                          <td colSpan={8} className='text-center py-4 text-muted'>
                            No transactions found
                          </td>
                        </tr>
                      ) : (
                        filteredItems.map((item) => (
                          <tr key={item._id}>
                            <td>{new Date(item.date || item.createdAt).toLocaleString()}</td>
                            <td>{item.narration}</td>
                            <td>{item.type}</td>
                            <td className='text-end'>₹ {item.credit.toFixed(2)}</td>
                            <td className='text-end'>₹ {item.debit.toFixed(2)}</td>
                            <td className='text-end'>₹ {item.balanceAfter.toFixed(2)}</td>
                            <td>{item.reference || '-'}</td>
                            <td>{item.addedBy || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Settlement Modal */}
      {showAdd && (
        <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className='modal-dialog modal-md modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header bg-primary text-white'>
                <h6 className='mb-0'>Add Settlement</h6>
                <button type='button' className='btn-close btn-close-white' onClick={() => setShowAdd(false)}></button>
              </div>
              <div className='modal-body'>
                <div className='mb-3'>
                  <label className='form-label'>Amount</label>
                  <input type='number' className='form-control' value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <div className='mb-3'>
                  <label className='form-label'>Type</label>
                  <select className='form-select' value={settleType} onChange={(e) => setSettleType(e.target.value as 'payout' | 'adjustment')}>
                    <option value='payout'>Payout (Pay To Affiliate)</option>
                    <option value='adjustment'>Adjustment</option>
                  </select>
                </div>
                <div className='mb-0'>
                  <label className='form-label'>Remarks</label>
                  <textarea className='form-control' rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-secondary' onClick={() => setShowAdd(false)}>Close</button>
                <button type='button' className='btn btn-primary' onClick={handleAddSettlement}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AffilateTransection
